import React, { useState } from 'react';
import { Box, Dialog, Divider, useMediaQuery, useTheme } from '@mui/material';
import * as turf from '@turf/turf';
import { IProduct } from '../../core/interfaces/product.interface';
import { IProject } from '../../core/interfaces/project.interface';
import { IAOIInfo } from '../../core/interfaces/aoi-info.interface';
import InstantHabmapImg from '../../Assets/instanthabmap.jpg';
import AdvancedHabmapImg from '../../Assets/advancedhabmap.jpg';
import BespokeHabmapImg from '../../Assets/bespokehabmap.jpg';
import { ProjectDialog } from '../ProjectDialog';
import { Res } from '../../resources';
import ukBorders from '../../Assets/uk-borders.json';
import { Feature, MultiPolygon } from 'geojson';
import { ProductCard } from '../ProductCard';
import { BulletItem } from '../BulletItem';
import useGentian from '../../core/hooks/useGentian';
import { isReseller } from '../../Utils/account.utils';

const ukPolygon = turf.multiPolygon((ukBorders as Feature<MultiPolygon>).geometry.coordinates);

export interface ProductsListProps {
  products?: IProduct[];
  AOIInfo?: IAOIInfo;
  onClose: () => void;
  onSuccess?(project: IProject): void;
  onSelectProduct?: (product: IProduct) => void;
  totalArea?: number;
  showCheckoutPopup?: boolean;
  setShowCheckoutPopup?: (open: boolean) => void;
  checkoutModalOpen?: boolean;
  setcheckoutModalOpen?: (open: boolean) => void;
}

const dataLayer = window['dataLayer'] || [];

export const ProductsList: React.FC<ProductsListProps> = ({
  products,
  AOIInfo,
  onClose,
  onSuccess,
  onSelectProduct,
  totalArea,
  showCheckoutPopup,
  setShowCheckoutPopup,
  checkoutModalOpen,
  setcheckoutModalOpen,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { data: accountData } = useGentian().useAccount();
  const isResellerAccount = isReseller(accountData);

  const [selectedProduct, setSelectedProduct] = useState<IProduct>(undefined);
  const [showProjectNameDialog, setShowProjectNameDialog] = useState(false);

  const handleOpenNameProject = (productId: string) => {
    const findSelectedProduct = products?.find((p) => p._id === productId);
    onSelectProduct?.(findSelectedProduct);
    setSelectedProduct(findSelectedProduct);
    setShowProjectNameDialog(true);
  };

  // Helper to determine if True Sight area is over 100ha
  const isTrueSightOver100ha =
    AOIInfo?.TotalArea > 100 && selectedProduct?._id === Res.ids.products.TRUE_SIGHT;

  const isTrueSightOver40ha = AOIInfo?.TotalArea > 40 && selectedProduct?._id === Res.ids.products.TRUE_SIGHT;

  const handleCloseNameProject = () => {
    setShowProjectNameDialog(false);
  };

  const handleCloseAll = () => {
    onClose();
    handleCloseNameProject();
  };

  // Makes sure the AOI is a single polygon lying entirely within Great Britain (the border asset
  // excludes Northern Ireland). Multi-part geometry is split into one Polygon per part on arrival by
  // geometryUtils.toSinglePolygons, so a MultiPolygon reaches here either as a single Polygon or as
  // several features that fail the count check above — there is no multi-part branch to handle.
  const isValidAOI = (aoiInfo: IAOIInfo) => {
    const polygonCount = aoiInfo?.shape?.features.length || 0;
    if (polygonCount > 1) {
      return false;
    }

    try {
      const geometry = aoiInfo?.shape?.features[0]?.geometry;

      if (geometry?.type !== 'Polygon') {
        return false;
      }

      return turf.booleanWithin(turf.polygon(geometry.coordinates), ukPolygon);
    } catch (error) {
      console.error('Error checking if AOI is within Great Britain:', error);
      return false;
    }
  };

  return (
    <Box
      sx={{
        display: 'flex',
        gap: '16px',
        flexDirection: isMobile ? 'column' : 'row',
        height: isMobile ? 'unset' : '100%',
      }}
    >
      <ProductCard
        title={products?.find((p) => p._id === Res.ids.products.TRUE_SIGHT)?.name}
        img={InstantHabmapImg}
        description="Instant habitat mapping for rapid BNG assessments across the UK"
        onButtonClick={() => handleOpenNameProject(Res.ids.products.TRUE_SIGHT)}
        link={Res.website_links.TRUE_SIGHT_DETAILS}
        isOnlyAvailableInUK
        disabled={!isValidAOI(AOIInfo)}
      >
        <BulletItem>Immediate results</BulletItem>
        <BulletItem>Results follow the statutory metric for biodiversity net gain (BNG)</BulletItem>
        <BulletItem>5-10 habitat classes (most common habitat types targeted for BNG estimate)</BulletItem>
        <BulletItem>Priority habitats included </BulletItem>
        <BulletItem>Linear features included </BulletItem>
        <BulletItem>Based on imagery up to 3 years old for recent results </BulletItem>
      </ProductCard>

      <Divider
        orientation={isMobile ? 'horizontal' : 'vertical'}
        flexItem
        sx={{ margin: isMobile ? '0 10px' : '10px 0' }}
      />

      <ProductCard
        title={products?.find((p) => p._id === Res.ids.products.TRUE_SAGE)?.name}
        img={AdvancedHabmapImg}
        description="Advanced habitat mapping reviewed by experts for reliability"
        onButtonClick={() => {
          if (dataLayer) {
            dataLayer.push({ event: 'true_sage_request' });
          }

          console.log('true sage');
          handleOpenNameProject(Res.ids.products.TRUE_SAGE);
        }}
        link={Res.website_links.TRUE_SAGE_DETAILS}
        isRequest
      >
        <BulletItem>Delivery within 10 working days</BulletItem>
        <BulletItem>
          Results follow statutory metrics for BNG (UK), EUNIS (Europe) or IUCN (Global).
        </BulletItem>
        <BulletItem>
          10-30 habitat classes (nearly all classes in the relevant habitat metric targeted)
        </BulletItem>
        <BulletItem>Priority habitats included </BulletItem>
        <BulletItem>Linear features included </BulletItem>
        <BulletItem>Based on imagery less than 1 year old for the most up-to-date results. </BulletItem>
      </ProductCard>

      {!isResellerAccount && (
        <>
          <Divider
            orientation={isMobile ? 'horizontal' : 'vertical'}
            flexItem
            sx={{ margin: isMobile ? '0 10px' : '10px 0' }}
          />
          <ProductCard
            title={products?.find((p) => p._id === Res.ids.products.TRUE_ORACLE)?.name}
            img={BespokeHabmapImg}
            description="Bespoke habitat mapping to meet your needs with expertly-tailored results"
            onButtonClick={() => {
              if (dataLayer) {
                dataLayer.push({ event: 'true_oracle_request' });
              }

              console.log('true oracleclicked');
              handleOpenNameProject(Res.ids.products.TRUE_ORACLE);
            }}
            link={Res.website_links.TRUE_ORACLE_DETAILS}
            isRequest
          >
            <BulletItem>
              Our bespoke habitat mapping service is tailored for clients with specific habitat mapping needs.
            </BulletItem>
            <BulletItem>
              We collaborate with you to create high-resolution maps based on site-specific data, ensuring
              your project&apos;s requirements are fully captured.
            </BulletItem>
            <BulletItem>
              Whether it&apos;s for conservation, land development or environmental assessments, our custom
              solution offers unmatched precision and flexibility.
            </BulletItem>
          </ProductCard>
        </>
      )}

      {showProjectNameDialog && (
        <Dialog
          open={showProjectNameDialog}
          onClose={handleCloseNameProject}
          disableScrollLock
          maxWidth="xl"
          PaperProps={{
            sx: {
              margin: '0',
            },
          }}
        >
          <ProjectDialog
            AOIInfo={AOIInfo}
            onClose={handleCloseNameProject}
            onSuccess={onSuccess}
            handleCloseAll={handleCloseAll}
            product={selectedProduct}
            isTrueSightOver100ha={isTrueSightOver100ha}
            isTrueSightOver40ha={isTrueSightOver40ha}
            showCheckoutPopup={showCheckoutPopup}
            setShowCheckoutPopup={setShowCheckoutPopup}
            checkoutModalOpen={checkoutModalOpen}
            setCheckoutModalOpen={setcheckoutModalOpen}
          />
        </Dialog>
      )}
    </Box>
  );
};
